import { ReactElement, useEffect, useState } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { FeatureFlags, FlagsProvider } from "flagged";
import { QueryClient, QueryClientProvider } from "react-query";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { getHtmlPageTitle } from "./helpers/titleHelpers";
import ErrorFallback from "./components/ErrorFallback/ErrorFallback";
import Layout from "./layout/layout";
import SessionProvider from "./components/session/SessionProvider";
import SessionService from "./services/sessions/sessions.service";
import LocationProvider from "./common/providers/locationProvider";
import { initialiseDateFormatter } from "./utils/dateFormatter";
import TransactionCartProvider from "./context/transactionCart/TransactionCartProvider";
import CustomerDetailsProvider from "./context/customerDetails/CustomerDetailsProvider";
import CompanyInfoProvider from "./context/companyInfo/CompanyInfoProvider";
import { getFeatureFlagsSettingsAsync } from "./helpers/featureFlagHelper";
import StripeKeysProvider from "./context/stripeKeys/StripeKeysProvider";
import AppInsightsProvider from "./components/AppInsights/AppInsightsProvider";
import { reactPlugin } from "./monitoring/app-insights/app-insights-config";

import "./theme/theme.scss";
import "./App.scss";

import LoadingPage from "./views/loading-page";
import { PreviewTokenProvider } from "./hooks/use-preview-token";
import { useTheme } from "./theme";
import { CONFIG } from "./config-global";

const locationProvider = new LocationProvider();
const sessionService = new SessionService(locationProvider);
const queryClient = new QueryClient();

// Load in the local locale for date/time formats
initialiseDateFormatter();

document.documentElement.className = "ptm";

// Set the theme attribute on the root html element to allow for theming via css variables
document.documentElement.dataset.theme = CONFIG.site.productOemId;

/*
 * The main entry point for the application.
 */
const App = (): ReactElement => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>();
  const theme = useTheme();

  // Retrieve feature flags from the API
  useEffect(() => {
    getFeatureFlagsSettingsAsync().then((flags) => {
      setFeatureFlags(flags);
    });
  }, []);

  // Update the fav icons
  useEffect(() => {
    const updateIcon = (id: string, icon: string) => {
      const faviconElement = document.getElementById(id) as HTMLLinkElement | null;
      if (faviconElement) {
        faviconElement.href = icon;
      }
    };
    theme.favIcons?.forEach((favIcon) => {
      updateIcon(favIcon.id, favIcon.src);
    });
  }, [theme.favIcons]);

  if (!featureFlags) {
    return <LoadingPage />;
  }

  return (
    <AppInsightsProvider reactPlugin={reactPlugin}>
      <HelmetProvider>
        <Helmet>
          <title>{getHtmlPageTitle()}</title>
        </Helmet>
        <AppInsightsErrorBoundary appInsights={reactPlugin} onError={ErrorFallback}>
          <FlagsProvider features={featureFlags}>
            <SessionProvider sessionService={sessionService}>
              <PreviewTokenProvider>
                <QueryClientProvider client={queryClient}>
                  <CompanyInfoProvider>
                    <CustomerDetailsProvider>
                      <TransactionCartProvider>
                        <StripeKeysProvider>
                          <Layout />
                        </StripeKeysProvider>
                      </TransactionCartProvider>
                    </CustomerDetailsProvider>
                  </CompanyInfoProvider>
                </QueryClientProvider>
              </PreviewTokenProvider>
            </SessionProvider>
          </FlagsProvider>
        </AppInsightsErrorBoundary>
      </HelmetProvider>
    </AppInsightsProvider>
  );
};
export default App;
